import React, { useMemo, useState } from "react";
import Button from "../ui/Button";
import { formatDateTime } from "src/utils/date-time";
import ContactSupportModal from "../modals/ContactSupportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { LogOut } from "lucide-react";
import * as authActions from "src/redux/slices/authTenantSlice";
import { useNavigate } from "react-router-dom";
import { logoutAndRedirect } from "src/utils/auth";
import usePlanUsage from "src/hooks/usePlanUsage";
import type { PlanUsagePlan } from "src/types/planUsage.types";

interface PlanExpireBannerProps {
  type?: "admin" | "tenant";
}

const PlanExpireBanner: React.FC<PlanExpireBannerProps> = ({
  type = "tenant",
}) => {
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const {
    user: { data: tenantUser },
  } = useAppSelector((state) => state.authTenant);
  const dispatch = useAppDispatch();
  const planSummary = tenantUser?.planSummary;
  const planStatus = planSummary?.planStatus;
  const planEndsAt = planSummary?.plan?.planEndsAt;
  const isTenantAdmin =
    tenantUser?.role === "admin" ||
    tenantUser?.roleDetails?.name?.toLowerCase() === "admin";
  const isTrialPlan =
    (planSummary?.plan?.planCode || "").toLowerCase() === "trial";
  const { planUsage } = usePlanUsage({
    enabled: type === "tenant" && !isTrialPlan,
  });

  const daysToExpire = useMemo(() => {
    if (!planEndsAt) return null;
    const diffMs = new Date(planEndsAt).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [planEndsAt]);

  const handleLogout = () => {
    dispatch(authActions.logout());
    logoutAndRedirect({ navigate });
  };

  const showExpiryBanner =
    type === "tenant" &&
    !isTrialPlan &&
    planStatus === "active" &&
    planEndsAt &&
    daysToExpire !== null &&
    daysToExpire <= 10 &&
    daysToExpire >= 0;

  const usageWarning = useMemo(() => {
    if (
      type !== "tenant" ||
      isTrialPlan ||
      planUsage?.planStatus !== "active"
    ) {
      return null;
    }
    const limits: Partial<NonNullable<PlanUsagePlan["features"]>> =
      planUsage?.plan?.features || {};
    const usage = planUsage?.usage || {};
    const entries = [
      {
        key: "Users",
        used: usage.totalUsers,
        limit: limits.totalUsers,
      },
      {
        key: "Products",
        used: usage.totalProducts,
        limit: limits.totalProducts,
      },
      {
        key: "Transactions",
        used: usage.totalTransactions,
        limit: limits.totalTransactions,
      },
    ].filter(
      (entry) =>
        typeof entry.limit === "number" &&
        entry.limit > 0 &&
        typeof entry.used === "number"
    );

    if (!entries.length) return null;

    const ranked = entries
      .map((entry) => ({
        ...entry,
        ratio: entry.used / (entry.limit || 1),
      }))
      .sort((a, b) => b.ratio - a.ratio);

    const top = ranked[0];
    if (top.ratio < 0.75) return null;
    return top;
  }, [planUsage, type]);

  const showExpiredScreen = type === "tenant" && planStatus === "expired";
  const showTrialBanner =
    type === "tenant" && planStatus === "active" && isTrialPlan;
  const formattedPlanEndsAt = planEndsAt
    ? formatDateTime(planEndsAt, "DD/MM/YYYY hh:mm:ss A")
    : null;

  return (
    <>
      {/* Topbar Header */}
      {showExpiryBanner && (
        <div className="sticky top-0 z-30 border-b border-amber-200 bg-amber-50">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm text-amber-900">
            <span>
              Your plan will be expired on {formattedPlanEndsAt || "soon"}
            </span>
            <Button
              variant="outline"
              color="warning"
              onClick={() => setIsSupportModalOpen(true)}
            >
              Extend Plan
            </Button>
          </div>
        </div>
      )}

      {showTrialBanner && (
        <div className="sticky top-0 z-30 border-b border-amber-200 bg-amber-50">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm text-amber-900">
            <span>
              You are on a Trial plan and it will expire on{" "}
              {formattedPlanEndsAt || "soon"}. Please upgrade to continue.
            </span>
            <Button
              variant="outline"
              color="warning"
              onClick={() => setIsSupportModalOpen(true)}
            >
              Upgrade
            </Button>
          </div>
        </div>
      )}

      {usageWarning && (
        <div className="sticky top-0 z-30 border-b border-blue-200 bg-blue-50">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm text-blue-900">
            <span>
              You have used {usageWarning.used} of {usageWarning.limit}{" "}
              {usageWarning.key} ({Math.round(usageWarning.ratio * 100)}%)
            </span>
            <Button
              variant="outline"
              color="info"
              onClick={() => setIsSupportModalOpen(true)}
            >
              Upgrade
            </Button>
          </div>
        </div>
      )}

      {/* full Screen Banner */}
      {showExpiredScreen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-white/95 p-6">
          <div className="max-w-lg text-center space-y-4">
            <h2 className="text-2xl font-semibold text-gray-900">
              Your plan expired kindly renew to continue
            </h2>
            <p className="text-sm text-gray-600">
              {isTenantAdmin
                ? "Please contact support to renew your plan and regain access."
                : "Please contact your admin for more info."}
            </p>
            <div className="flex items-center justify-center gap-4">
              {isTenantAdmin && (
                <Button onClick={() => setIsSupportModalOpen(true)}>
                  Renew
                </Button>
              )}

              <Button variant="primary" color="danger" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Sign out</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <ContactSupportModal
        open={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </>
  );
};

export default PlanExpireBanner;
